import { useContext, useState } from 'react';
import { ApartmentViewContext } from '../../context/ApartmentViewContext';
import { Context } from '../../../../Context';
import { useTranslation } from 'react-i18next';
import {
	useCompartmentFiltering,
	useCompartmentInteractions,
	useCompartmentData,
} from './hooks';
import { getModeConfig } from '../../config';
import { InfoModeGridView, ArrangeModeGridView } from './modes';
import { 
	infoModeName, 
	stylesModeName, 
	insolationModeName,
	arrangeModeName,
	isometricModeName,
	hotSpotModeName
} from '../../context/contextStrings';

import { ReactComponent as IconChevron } from '../../../../media/img/icon-chevron.svg';

import styles from '../../../../styles/aprt.module.scss';
import apart_styles from '../../../../styles/aprt.module.scss';
import buttons_styles from '../../../../styles/buttons.module.scss';

/**
 * CompartmentsBlock with new Grid-based UI
 * Uses InfoModeGridView for tile-based layout
 *
 * This is a simplified version focusing on InfoMode with new UI
 */
function CompartmentsBlockWithGrid({ apartmentViewMode, apartmentData }) {
	const { t } = useTranslation();
	const { mode } = apartmentViewMode;
	const { apartmentViewInteractions } = useContext(ApartmentViewContext);

	const [expandedCompartmentList, setExpandedCompartmentList] = useState(true);

	const { hoveredCompartmentInd, selectedCompartmentInd } =
		apartmentViewInteractions;

	// Get mode configuration
	const modeConfig = getModeConfig(mode);

	// Use hooks to get data and handlers
	const { indoorCompartments, outdoorCompartments } = useCompartmentFiltering(
		apartmentData,
		mode
	);

	const { 
		getCompartmentName, 
		getOutdoorCompartmentName,
		arrangementVersions,
	} = useCompartmentData(apartmentData);


	const { userSelectedArrangements, urlComboParam } = useContext(ApartmentViewContext);
	
	const interactions = useCompartmentInteractions(mode, arrangementVersions);
	const { handleRoomClickInArrangeMode } = interactions;

	// Helper to get compartment name (handles both indoor and outdoor)
	const getCompartmentDisplayName = (compartment) => {
		// Check if it's outdoor compartment (has 'name' instead of 'name_cad')
		if (compartment.name && !compartment.name_cad) {
			return getOutdoorCompartmentName(compartment);
		}
		return getCompartmentName(compartment);
	};

	// Don't render if mode config says not to show compartments list
	if (!modeConfig.compartmentsList.show) {
		return null;
	}

	// Render based on mode
	const renderContent = () => {
		// For InfoMode, use new Grid View
		if (
			mode === infoModeName || 
			mode === isometricModeName || 
			mode === stylesModeName || 
			mode === insolationModeName ||
			mode === hotSpotModeName
		) {
			return (
				<InfoModeGridView
					indoorCompartments={indoorCompartments}
					outdoorCompartments={outdoorCompartments}
					interactions={interactions}
					selectedCompartmentInd={selectedCompartmentInd}
					hoveredCompartmentInd={hoveredCompartmentInd}
					getCompartmentName={getCompartmentDisplayName}
					columnsPerRow={3} // Can be made responsive or configurable
				/>
			);
		}

		// For ArrangeMode, use ArrangeMode Grid View
		if (mode === arrangeModeName) {


			// Calculate number of possible arrangements
			const numberOfPossibleArrangements = arrangementVersions.reduce(
				(acc, num) => acc * num,
				1
			);

			return (
				<ArrangeModeGridView
					indoorCompartments={indoorCompartments}
					outdoorCompartments={outdoorCompartments}
					interactions={interactions}
					hoveredCompartmentInd={hoveredCompartmentInd}
					getCompartmentName={getCompartmentDisplayName}
					handleRoomClickInArrangeMode={handleRoomClickInArrangeMode}
					arrangementVersions={arrangementVersions}
					userSelectedArrangements={userSelectedArrangements}
					urlComboParam={urlComboParam}
					numberOfPossibleArrangements={numberOfPossibleArrangements}
					columnsPerRow={3}
				/>
			);
		}

		// For other modes, show placeholder
		// TODO: Implement other mode views
		return (
			<div className={styles.compartmentsList__block}>
				<p>
					{t(
						'mode_view_coming_soon',
						`Widok dla trybu "${mode}" w trakcie budowy...`
					)}
				</p>
			</div>
		);
	};

	return (
		<div className={styles.compartmentsList__block} id='compartments-block'>
			<div // ----------------------------- desc header -----------------------------
				style={{
					width: '100%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '2.5rem',
					wrap: 'nowrap',
					paddingLeft: '0.5rem',
				}}
			>
				<h3 className={styles.heading__compartments}>{t(modeConfig.title)}</h3>

				{/* <CustomToolTip
						title={'Pokaż materiały dodatkowe do pobrania'}
						slots={{
							transition: Zoom,
						}}
						arrow
					> */}
				<button
					className={buttons_styles['bim_button--no_border']}
					onClick={() => setExpandedCompartmentList(!expandedCompartmentList)}
				>
					<IconChevron
						title={'pokaż/ukryj opis'}
						className={buttons_styles['bim_button__icon']}
						style={{
							transform: expandedCompartmentList
								? 'rotate(-90deg)'
								: 'rotate(90deg)',
							transition: 'transform 300ms ease-in-out',
						}}
					/>
				</button>
				{/* </CustomToolTip> */}
			</div>

			<div
				style={{
					width: '100%',
					maxHeight: expandedCompartmentList ? '2000px' : '0',
					opacity: expandedCompartmentList ? 1 : 0,
					overflow: 'hidden',
					transition: 'max-height 300ms ease-in-out, opacity 300ms ease-in-out',
				}}
			>
				{renderContent()}
			</div>
		</div>
	);
}

export default CompartmentsBlockWithGrid;
